type PersistCaption = (
  imageId: string,
  caption: string | undefined
) => Promise<void>;

interface PendingCaptionUpdate {
  latestCaption: string;
  task: Promise<void>;
}

const normalizeCaption = (caption: string): string | undefined =>
  caption.trim() || undefined;

export class CaseImageCaptionUpdateQueue {
  private readonly pending = new Map<string, PendingCaptionUpdate>();

  private readonly failures = new Set<string>();

  private readonly latestCaptions = new Map<string, string>();

  constructor(private readonly persistCaption: PersistCaption) {}

  enqueue(imageId: string, caption: string): void {
    this.latestCaptions.set(imageId, caption);
    this.failures.delete(imageId);
    const current = this.pending.get(imageId);
    if (current) {
      current.latestCaption = caption;
      return;
    }
    this.startUpdate(imageId, caption);
  }

  async flush(imageId?: string): Promise<void> {
    this.retryFailedUpdates(imageId);
    const updates = imageId
      ? [this.pending.get(imageId)?.task]
      : Array.from(this.pending.values()).map((update) => update.task);
    await Promise.all(updates.filter(Boolean));
    if (this.hasFailure(imageId)) throw new Error("Caption update failed");
  }

  private startUpdate(imageId: string, caption: string): void {
    const update: PendingCaptionUpdate = {
      latestCaption: caption,
      task: Promise.resolve(),
    };
    this.pending.set(imageId, update);
    update.task = this.persistLatest(imageId, update)
      .catch(() => {
        this.failures.add(imageId);
      })
      .finally(() => this.removeCompletedUpdate(imageId, update));
  }

  private persistLatest(
    imageId: string,
    update: PendingCaptionUpdate
  ): Promise<void> {
    const persistedCaption = update.latestCaption;
    return this.persistCaption(
      imageId,
      normalizeCaption(persistedCaption)
    ).then(() => {
      if (persistedCaption === update.latestCaption) return;
      return this.persistLatest(imageId, update);
    });
  }

  private retryFailedUpdates(imageId?: string): void {
    const failedImageIds = imageId ? [imageId] : Array.from(this.failures);
    failedImageIds.forEach((failedImageId) => {
      const caption = this.latestCaptions.get(failedImageId);
      if (!this.failures.has(failedImageId) || caption === undefined) return;
      this.failures.delete(failedImageId);
      this.startUpdate(failedImageId, caption);
    });
  }

  private removeCompletedUpdate(
    imageId: string,
    update: PendingCaptionUpdate
  ): void {
    if (this.pending.get(imageId) === update) this.pending.delete(imageId);
  }

  private hasFailure(imageId?: string): boolean {
    return imageId ? this.failures.has(imageId) : this.failures.size > 0;
  }
}
